import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getPublicProject } from "../lib/api";
import { SiteFooter } from "../components/SiteFooter";

// Unauthenticated read-only gallery for a published project, reached at
// /p/<shareToken>. Anyone with the link can view the widgets (no sign-in).
export function PublicProject() {
  const { token } = useParams<{ token: string }>();
  const q = useQuery({
    queryKey: ["public-project", token],
    queryFn: () => getPublicProject(token!),
    enabled: !!token,
    retry: false,
  });

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-bg-2 px-6 py-3 flex items-center gap-3">
        <Link to="/" className="font-semibold tracking-tight">
          MCPBuild
        </Link>
        <span className="text-fg-2 text-xs font-mono">shared gallery</span>
        <Link
          to="/"
          className="ml-auto text-xs text-accent hover:underline"
        >
          Build your own →
        </Link>
      </header>

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
          {q.isLoading && <div className="text-fg-1 text-sm">Loading…</div>}

          {q.error && (
            <div className="rounded-md border border-bg-2 bg-bg-1 p-6 text-fg-1 text-sm">
              This shared link isn't available — it may have been unpublished or
              never existed.
            </div>
          )}

          {q.data && (
            <>
              <div>
                <div className="text-2xl mb-1">{q.data.project.icon ?? "🧩"}</div>
                <h1 className="text-2xl font-semibold">{q.data.project.name}</h1>
                <p className="text-fg-2 text-sm mt-1">
                  {q.data.tools.length} widget{q.data.tools.length === 1 ? "" : "s"} ·
                  read-only gallery
                </p>
              </div>

              {token && (
                <div className="rounded-lg border border-bg-2 bg-bg-1 p-4 space-y-2">
                  <div className="text-fg-0 text-sm font-semibold">
                    Use these in Claude — no sign-in
                  </div>
                  <p className="text-fg-2 text-xs">
                    Add this as a custom connector (Settings → Connectors) and
                    Claude can call these widgets as tools.
                  </p>
                  <code className="block bg-bg-2 border border-bg-2 rounded-md px-3 py-2 text-fg-0 text-xs font-mono break-all">
                    {`${window.location.origin}/mcp/public/${token}`}
                  </code>
                  <div className="pt-2 border-t border-bg-2 mt-2">
                    <div className="text-fg-0 text-sm font-semibold">
                      Or use them in a browser agent (WebMCP)
                    </div>
                    <p className="text-fg-2 text-xs mt-0.5 mb-1.5">
                      Download a browser extension that exposes these tools to an
                      in-browser AI agent (experimental — needs Chrome's WebMCP flag).
                    </p>
                    <a
                      href={`${window.location.origin}/api/public/${token}/extension.zip`}
                      className="inline-block px-3 py-1.5 rounded-md text-xs bg-accent text-white hover:opacity-90"
                    >
                      ⬇ Download WebMCP extension (.zip)
                    </a>
                  </div>
                </div>
              )}

              {q.data.tools.length === 0 && (
                <div className="rounded-md border border-bg-2 bg-bg-1 p-6 text-fg-2 text-sm">
                  This project has no renderable widgets yet.
                </div>
              )}

              <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
                {q.data.tools.map((t) => (
                  <div
                    key={t.toolSlug}
                    className="relative rounded-lg overflow-hidden border border-bg-2 bg-white aspect-[4/3]"
                    title={t.description ?? t.toolSlug}
                  >
                    {t.htmlUrl ? (
                      <div className="absolute inset-0 overflow-hidden">
                        <iframe
                          src={t.htmlUrl}
                          title={t.toolSlug}
                          sandbox="allow-scripts allow-same-origin"
                          className="origin-top-left"
                          style={{ width: "200%", height: "200%", transform: "scale(0.5)", border: 0 }}
                        />
                      </div>
                    ) : (
                      <div className="absolute inset-0 grid place-items-center text-fg-2 text-sm bg-bg-1">
                        (no preview)
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 px-3 py-2 bg-gradient-to-t from-black/85 via-black/60 to-transparent text-white">
                      <div className="text-sm font-medium leading-tight truncate">
                        {t.description ?? t.toolSlug}
                      </div>
                      <div className="mt-0.5 text-[10px] font-mono opacity-80 truncate">
                        {t.toolSlug}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
